/**
 * Cash Position (BE-14a)
 *
 * Live: every active bank, cash drawer and e-wallet balance, what the credit cards owe and the issued cheques not
 * yet cleared. An account opens its Bank Statement; a card opens its page.
 * Route: /reports/cash-position
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Button, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { SummaryCardGrid, DataTable, CurrencyText, ReportExportMenu } from '../../components/accounting';
import { serverMessage } from '../../utils/apiConvert';
import { financeReportsService } from '../../services/accounting/financeReports.service';

const TYPE_LABELS = { BANK: 'Bank', CASH: 'Cash drawer', WALLET: 'E-wallet', CREDIT_CARD: 'Credit card' };

const CashPosition = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await financeReportsService.getCashPosition());
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the cash position'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCash = Number(data?.totalCash || 0);
  const cardsOwed = Number(data?.cardsOwed || 0);
  const cheques = Number(data?.outstandingChequeTotal || 0);
  const free = totalCash - cardsOwed - cheques;

  const cards = [
    { label: 'Total Cash', value: totalCash, color: 'success' },
    { label: 'Cards Owed', value: cardsOwed, color: cardsOwed > 0 ? 'error' : undefined },
    {
      label: `Uncleared Cheques (${data?.outstandingChequeCount || 0})`, value: cheques, color: cheques > 0 ? 'warning' : undefined,
      onClick: cheques > 0 ? () => navigate('/banking?tab=cheques') : undefined,
    },
    { label: 'Cash after cards and cheques', value: free, color: free < 0 ? 'error' : 'info' },
  ];

  const accountColumns = [
    { key: 'name', label: 'Account', width: '50%' },
    { key: 'type', label: 'Type', width: '25%', render: (r) => TYPE_LABELS[r.type] || r.type },
    { key: 'balance', label: 'Balance', width: '25%', align: 'right', render: (r) => <CurrencyText amount={r.balance} bold /> },
  ];
  const cardColumns = [
    { key: 'name', label: 'Card', width: '75%' },
    { key: 'balance', label: 'Owed', width: '25%', align: 'right', render: (r) => <CurrencyText amount={r.balance} bold /> },
  ];

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Cash Position</Typography>
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Live balances of every active account and card</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <ReportExportMenu reportType="cash-position" params={{}} onError={setError} />
        <Tooltip title="Refresh">
          <Button size="small" variant="text" onClick={load} startIcon={<Refresh />} sx={{ textTransform: 'none', fontSize: fs.xs }}>
            Refresh
          </Button>
        </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      <SummaryCardGrid cards={cards} loading={loading && !data} />

      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mt: 2, mb: 1 }}>Accounts</Typography>
      <DataTable
        columns={accountColumns}
        data={(data?.accounts || []).map((a) => ({ ...a, id: a.accountId }))}
        loading={loading && !data}
        pagination={false}
        onRowClick={(r) => navigate(`/reports/wallet-bank?account=${r.accountId}`)}
        emptyMessage="No active bank, cash or e-wallet account."
      />

      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mt: 2, mb: 1 }}>Credit Cards</Typography>
      <DataTable
        columns={cardColumns}
        data={(data?.cards || []).map((c) => ({ ...c, id: c.accountId }))}
        loading={loading && !data}
        pagination={false}
        onRowClick={(r) => navigate(`/accounting/credit-cards/${r.accountId}`)}
        emptyMessage="No active credit card."
      />
    </Box>
  );
};

export default CashPosition;
